import _ from "lodash";
import React, { useState } from "react";
import { useI18n } from "Hooks";

import { Tabs } from "@toddle-edu/ds-web";

import { PARENT, STAFF, STUDENT } from "Constants/stringConstants";
import { isIdEqual } from "Utils";

import classes from "./ViewMembers.scss";
import UserTypeList from "../UserTypeList";

const getInitialUserTypeTab = ({ usersByType, loadingStateByType }) => {
  const tabByUsersPresent = _.first(_.keys(usersByType));
  const tabByLoadingState = _.first(_.keys(loadingStateByType));
  return tabByUsersPresent || tabByLoadingState;
};

const tabContainerStyles = { paddingBlock: "12px" };

const TAB_USER_TYPE_ORDER = [STAFF, STUDENT, PARENT];

const userTypeLabels = {
  [STUDENT]: "common:student",
  [PARENT]: "common:family_member",
  [STAFF]: "common:staff_member",
};

const userTypeLabelsPlural = {
  [STUDENT]: "common:student_plural",
  [PARENT]: "common:family_member_plural",
  [STAFF]: "common:staff_members_plural",
};

const getUserTypeLabels = ({ totalUsersCount, userType, t }) =>
  totalUsersCount === 1
    ? t(userTypeLabels[userType])
    : t(userTypeLabelsPlural[userType]);

const getUserTypeList = ({ users }) => {
  const usersByType = _.groupBy(users, "type");

  const userTypeList = _.reduce(
    TAB_USER_TYPE_ORDER,
    (acc, userType) => {
      if (usersByType[userType]) {
        acc[userType] = usersByType[userType];
      }
      return acc;
    },
    {}
  );

  return userTypeList;
};

const getUserTypeTabs = ({ t, usersByType, loadingStateByType }) => {
  const usersByPresentUsersTabs = _.map(_.keys(usersByType), userType => {
    const totalUsersCount = _.size(usersByType[userType]);

    return {
      label: `${getUserTypeLabels({
        totalUsersCount,
        t,
        userType,
      })} (${totalUsersCount})`,
      value: userType,
    };
  });
  // if keys not present in userByTYpe tab and present in loadingStateByType then add it to tabs
  const loadingStateByTypeKeys = _.keys(loadingStateByType);
  const userTypeTab = _.reduce(
    loadingStateByTypeKeys,
    (acc, userType) => {
      if (!usersByType[userType]) {
        if (loadingStateByType[userType]) {
          acc.push({
            label: `${getUserTypeLabels({
              totalUsersCount: 0,
              t,
              userType,
            })} (0)`,
            value: userType,
          });
        }
      }
      return acc;
    },
    usersByPresentUsersTabs
  );

  return _.reduce(
    TAB_USER_TYPE_ORDER,
    (acc, userType) => {
      const tab = _.find(userTypeTab, { value: userType });
      if (tab) {
        acc.push(tab);
      }
      return acc;
    },
    []
  );
};

const ViewMembers = props => {
  const {
    users,
    handleRemoveUser,
    updateUsers,
    threadsContainerStyles,
    isAddMembers,
    isMemberUpdate = true,
    loadingStateByType = {},
  } = props;

  const usersByType = getUserTypeList({ users });

  const { t } = useI18n(["common"]);

  const [currentUserType, setCurrentUserType] = useState(
    getInitialUserTypeTab({ usersByType, loadingStateByType })
  );

  const handleTabChange = value => {
    setCurrentUserType(value);
  };

  const onRemoveClick = async ({ user }) => {
    if (isAddMembers) {
      const updatedUser = _.filter(users, u => {
        return !isIdEqual(user.id, u.id);
      });

      updateUsers(updatedUser);
    } else {
      handleRemoveUser({ user });
    }
  };

  const isLoading = loadingStateByType[currentUserType];

  return (
    <div className={classes.viewMembersContainer}>
      <div className={classes.tabContainer}>
        <Tabs
          options={getUserTypeTabs({ t, usersByType, loadingStateByType })}
          onChange={handleTabChange}
          value={currentUserType}
          containerStyle={tabContainerStyles}
          hasSeparator={true}
        />
      </div>
      <div
        className={classes.threadsContainerWrapper}
        style={threadsContainerStyles}
      >
        <UserTypeList
          users={usersByType[currentUserType]}
          userType={currentUserType}
          onRemoveClick={onRemoveClick}
          isMemberUpdate={isMemberUpdate}
          isUpdateFlow={!isAddMembers}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};

export default ViewMembers;
